import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authFetch } from '@/api/request';
import { setToken, setUserInfo } from '@/utils/auth';

// 模块级标志，防止 React StrictMode 重复发起回调请求
let callbackInProgress = false;

export function AuthCallbackPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 防止 React StrictMode 开发模式下重复执行
    if (callbackInProgress) return;
    callbackInProgress = true;

    // 先检查 URL 参数是否有历史错误
    const urlError = searchParams.get('error');
    if (urlError) {
      setError(urlError);
      setLoading(false);
      return;
    }

    const code = searchParams.get('code');
    if (!code) {
      setError('缺少授权码，请重新登录');
      setLoading(false);
      return;
    }

    authFetch('/api/auth/feishu/callback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || `登录失败 (${res.status})`);
        }
        const { token, user } = data;
        if (token) {
          setToken(token);
          if (user) setUserInfo(user);
          navigate('/', { replace: true });
        } else {
          setError('登录响应异常，请重试');
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        const msg = err.message || '登录失败，请重试';
        console.error('[AuthCallback] Login error:', err);
        setError(msg);
        setLoading(false);
        // 将错误信息写入 URL 参数，刷新后仍可见
        const url = new URL(window.location.href);
        url.searchParams.set('error', msg);
        window.history.replaceState(null, '', url.toString());
      });
  }, [navigate, searchParams]);

  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 tech-grid">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-border border-t-neon-cyan" />
        <span className="text-sm text-muted-foreground">正在完成登录...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-5 tech-grid">
        <div className="flex w-[400px] flex-col items-center rounded-2xl border border-border/60 bg-[hsl(var(--card)/0.7)] px-9 py-10 text-center backdrop-blur-xl">
          <div className="mb-4 text-4xl">😥</div>
          <h2 className="mb-2 text-lg font-semibold">登录失败</h2>
          <p className="mb-6 text-[13px] text-red-400">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="rounded-xl bg-[hsl(var(--primary)/0.15)] px-8 py-2.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-[hsl(var(--primary)/0.25)]"
          >
            返回登录
          </button>
        </div>
      </div>
    );
  }

  return null;
}
